import { min } from 'lib/array/arrayUtils'
import debounce from 'lodash.debounce'
import { useCallback, useEffect, useMemo, useRef } from 'react'

function referencePoint(
  rect: DOMRect,
  alignment: 'start' | 'center' | 'end',
) {
  if (alignment === 'start') {
    return rect.left
  } else if (alignment === 'end') {
    return rect.right
  } else {
    return rect.left + rect.width / 2
  }
}

function objectOrArrayKeys<T extends {}>(obj: T): Array<keyof T> {
  if (Array.isArray(obj)) {
    return obj.map((_, index) => index) as Array<keyof T>
  } else {
    return Object.keys(obj) as Array<keyof T>
  }
}

function useDetectCarouselSlide<
  SlideElementsObj extends Array<HTMLElement | null> | Record<string, HTMLElement | null>,
>(
  carouselRef: React.RefObject<HTMLElement | null>,
  slideElementsRef: React.RefObject<SlideElementsObj>,
  onSlideChange: (slideKey: SlideElementsObj extends Array<any> ? number : string) => void,
  alignment: 'start' | 'center' | 'end' = 'center',
): () => void {
  // Update selected item after swiping to a different card
  // Debounce to avoid updating item before smooth scroll has finished
  const onScrollNotSwiping = useMemo<() => void>(
    () =>
      debounce(() => {
        const carousel = carouselRef.current
        const slideElements = slideElementsRef.current
        if (!carousel || !slideElements) { return }
        const carouselRect = carousel.getBoundingClientRect()
        const selectedKey = min(objectOrArrayKeys(slideElements), (index) => {
          // @ts-expect-error There's something wrong with the returning type of `objectOrArrayKeys`
          const slideRect = slideElements[index]?.getBoundingClientRect()
          if (!slideRect) { return Number.MAX_SAFE_INTEGER }
          const cardCentre = referencePoint(slideRect, alignment)
          const carouselCentre = referencePoint(carouselRect, alignment)
          return Math.abs(cardCentre - carouselCentre)
        })
        if (typeof selectedKey !== 'undefined') {
          // @ts-expect-error There's something wrong with the returning type of `objectOrArrayKeys`
          onSlideChange(selectedKey)
        }
      }, 100),
  [carouselRef, onSlideChange, slideElementsRef, alignment],
  )

  // Don't update item mid-swipe
  const isSwiping = useRef(false)
  useEffect(() => {
    const carouselElement = carouselRef.current
    const startSwipe = () => {
      isSwiping.current = true
    }
    const endSwipe = () => {
      isSwiping.current = false
      onScrollNotSwiping()
    }
    if (carouselElement) {
      carouselElement.addEventListener('touchstart', startSwipe)
      carouselElement.addEventListener('touchend', endSwipe)
      return () => {
        carouselElement.removeEventListener('touchstart', startSwipe)
        carouselElement.removeEventListener('touchend', endSwipe)
      }
    }
  }, [carouselRef, onScrollNotSwiping])

  const onScroll = useCallback(() => {
    if (!isSwiping.current) {
      onScrollNotSwiping()
    }
  }, [onScrollNotSwiping])

  return onScroll
}

export default useDetectCarouselSlide
